
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  source: 'endpoint',
  description: 'Use the webhook events endpoint to recover webhooks your endpoint may have missed.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Webhook events' subtitle='Recover webhooks your endpoint missed' mdxType="Header" />
    <h4 {...{
      "id": "betawebhook_eventslist"
    }}>{`/beta/webhook_events/list`}</h4>
    <Schema method='POST' route='/beta/webhook_events/list' type='request' mdxType="Schema">
      <MDXCodeBlock type='server' title='/beta/webhook_events/list' mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-node"
          }}>{`// Provide a cursor you've previously stored, or leave it
// undefined on the first call.
let cursor = loadSavedCursor();
let hasMore = true;

while (hasMore) {
  const request: BetaWebhookEventsListRequest = {
    cursor: cursor,
    count: 100,
  };
  const response = await plaidClient.betaWebhookEventsList(request);
  const data = response.data;

  for (const event of data.webhook_events) {
    if (!alreadyProcessed(event.webhook_message_id)) {
      handleWebhookEvent(event);
    }
  }

  hasMore = data.has_more;

  // Persist next_cursor even when has_more is false, then reuse
  // it on the next poll so you only receive newer events.
  cursor = data.next_cursor;
  saveCursor(cursor);
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`curl -X POST https://sandbox.plaid.com/beta/webhook_events/list \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "count": Number,
  "cursor": String
}'
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-ruby"
          }}>{`# Provide a cursor you've previously stored, or leave it nil on
# the first call.
cursor = load_saved_cursor
has_more = true

while has_more
  request = Plaid::BetaWebhookEventsListRequest.new(
    {
      cursor: cursor,
      count: 100,
    }
  )
  response = client.beta_webhook_events_list(request)

  response.webhook_events.each do |event|
    unless already_processed(event.webhook_message_id)
      handle_webhook_event(event)
    end
  end

  has_more = response.has_more

  # Persist next_cursor even when has_more is false, then reuse
  # it on the next poll so you only receive newer events.
  cursor = response.next_cursor
  save_cursor(cursor)
end
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-java"
          }}>{`// Provide a cursor you've previously stored, or leave it null on
// the first call.
String cursor = loadSavedCursor();
boolean hasMore = true;

while (hasMore) {
  BetaWebhookEventsListRequest request = new BetaWebhookEventsListRequest()
    .cursor(cursor)
    .count(100);
  Response<BetaWebhookEventsListResponse> response = client()
    .betaWebhookEventsList(request)
    .execute();
  BetaWebhookEventsListResponse data = response.body();

  for (WebhookEvent event : data.getWebhookEvents()) {
    if (!alreadyProcessed(event.getWebhookMessageId())) {
      handleWebhookEvent(event);
    }
  }

  hasMore = data.getHasMore();

  // Persist next_cursor even when has_more is false, then reuse
  // it on the next poll so you only receive newer events.
  cursor = data.getNextCursor();
  saveCursor(cursor);
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-python"
          }}>{`# Provide a cursor you've previously stored, or leave it None on
# the first call.
cursor = load_saved_cursor()
has_more = True

while has_more:
    request = BetaWebhookEventsListRequest(
        cursor=cursor,
        count=100,
    )
    response = client.beta_webhook_events_list(request)

    for event in response['webhook_events']:
        if not already_processed(event['webhook_message_id']):
            handle_webhook_event(event)

    has_more = response['has_more']

    # Persist next_cursor even when has_more is false, then reuse
    # it on the next poll so you only receive newer events.
    cursor = response['next_cursor']
    save_cursor(cursor)
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go"
          }}>{`// Provide a cursor you've previously stored, or leave it nil on
// the first call.
cursor := loadSavedCursor()
hasMore := true

for hasMore {
    request := plaid.NewBetaWebhookEventsListRequest()
    request.SetCount(100)
    if cursor != nil {
        request.SetCursor(*cursor)
    }

    resp, _, err := client.PlaidApi.BetaWebhookEventsList(ctx).BetaWebhookEventsListRequest(*request).Execute()
    if err != nil {
        // Handle error
        break
    }

    for _, event := range resp.GetWebhookEvents() {
        if !alreadyProcessed(event.GetWebhookMessageId()) {
            handleWebhookEvent(event)
        }
    }

    hasMore = resp.GetHasMore()

    // Persist next_cursor even when has_more is false, then reuse
    // it on the next poll so you only receive newer events.
    nextCursor := resp.GetNextCursor()
    cursor = &nextCursor
    saveCursor(cursor)
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-go",
            "metastring": "sdk=fern",
            "sdk": "fern"
          }}>{`// Provide a cursor you've previously stored, or leave it nil on
// the first call.
cursor := loadSavedCursor()
hasMore := true

for hasMore {
    request := &plaid.BetaWebhookEventsListRequest{
        Count: plaid.Int(100),
    }
    if cursor != nil {
        request.Cursor = cursor
    }

    page, err := client.WebhookEvents.List(ctx, request)
    if err != nil {
        // Handle error
        break
    }
    resp := page.Response

    for _, event := range resp.GetWebhookEvents() {
        if !alreadyProcessed(event.GetWebhookMessageID()) {
            handleWebhookEvent(event)
        }
    }

    hasMore = resp.GetHasMore()

    // Persist next_cursor even when has_more is false, then reuse
    // it on the next poll so you only receive newer events.
    nextCursor := resp.GetNextCursor()
    cursor = &nextCursor
    saveCursor(cursor)
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <Schema method='POST' route='/beta/webhook_events/list' type='response' mdxType="Schema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "betawebhook_eventslist",
        level: 4,
        title: "/beta/webhook_events/list",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
